import { useState, useEffect } from 'react'
import { MdPerson, MdAdd, MdSchedule, MdRefresh, MdWarning } from 'react-icons/md'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Button, LoadingSpinner, SearchBar, FilterBar, StatCard } from '../../components/ui'
import { useFlightAttendant } from '../../hooks/useFlightAttendant'
import { useWebsocket } from '../../hooks/useWebsocket'
import FlightAttendantList from '../../modules/flight-attendants/FlightAttendantList'
import { addSubscription, removeSubscription } from '../../state/websocket.slice'
import { getWebsocketChannel } from '../../utils/websocket'

function FlightAttendantPage() {
  const [flightAttendants, setFlightAttendants] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const dispatch = useDispatch()
  const { subscribeToChannel, unsubscribeFromChannel } = useWebsocket()
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    onLeave: 0,
    terminated: 0,
  })

  const navigate = useNavigate()
  const { getFlightAttendantsAsync, isGetFlightAttendantsLoading } = useFlightAttendant()

  const loadFlightAttendants = async () => {
    try {
      setIsLoading(true)
      const flightAttendantsData = await getFlightAttendantsAsync()
      setFlightAttendants(flightAttendantsData || [])
      setStats({
        total: flightAttendantsData.length,
        active: flightAttendantsData.filter((fa) => fa.status === 'active').length,
        onLeave: flightAttendantsData.filter((fa) => fa.status === 'on_leave').length,
        terminated: flightAttendantsData.filter((fa) => fa.status === 'terminated').length,
      })
    } catch (error) {
      console.error('Error loading flight attendants:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadFlightAttendants()

    const channelName = getWebsocketChannel().flightCrewList

    if (channelName) {
      subscribeToChannel(channelName)
      dispatch(addSubscription(channelName))
    }

    return () => {
      if (channelName) {
        unsubscribeFromChannel(channelName)
        dispatch(removeSubscription(channelName))
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const getFilteredFlightAttendants = () => {
    let filtered = flightAttendants
    if (statusFilter !== 'all') {
      filtered = filtered.filter((fa) => fa.status === statusFilter)
    }
    if (searchTerm) {
      filtered = filtered.filter(
        (fa) =>
          fa.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          fa.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          fa.employeeNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          fa.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          fa.airline?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          fa.baseAirport?.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    }
    return filtered
  }

  const filteredFlightAttendants = getFilteredFlightAttendants()

  const handleAddFlightAttendant = () => {
    navigate('/flight-attendants/new')
  }

  const handleRefresh = () => {
    loadFlightAttendants()
  }

  const handleViewFlightAttendant = (flightAttendant) => {
    navigate(`/flight-attendants/${flightAttendant._id}`)
  }

  const handleEditFlightAttendant = (flightAttendant) => {
    navigate(`/flight-attendants/${flightAttendant._id}/edit`)
  }

  const handleDeleteFlightAttendant = (flightAttendant) => {
    // TODO: Implement delete confirmation modal
    console.log('Delete flight attendant:', flightAttendant._id)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="text-left">
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-white mb-2">
              Flight Attendant Management
            </h1>
            <p className="text-neutral-600 dark:text-neutral-400">
              Monitor and manage flight crew assignments and schedules
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              onClick={handleRefresh}
              disabled={isGetFlightAttendantsLoading}
              className="flex items-center space-x-2"
            >
              <MdRefresh className={isGetFlightAttendantsLoading ? 'animate-spin' : ''} />
              <span>Refresh</span>
            </Button>
            <Button
              variant="primary"
              onClick={handleAddFlightAttendant}
              className="flex items-center space-x-2"
            >
              <MdAdd className="text-lg" />
              <span>Add Flight Attendant</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Flight Attendants"
          value={stats.total}
          subtitle="Registered crew members"
          icon={MdPerson}
          iconBgColor="bg-primary-100 dark:bg-primary-900"
          iconColor="text-primary-600 dark:text-primary-400"
        />
        <StatCard
          title="Active Crew"
          value={stats.active}
          subtitle="Currently working"
          icon={MdSchedule}
          iconBgColor="bg-success-100 dark:bg-success-900"
          iconColor="text-success-600 dark:text-success-400"
        />
        <StatCard
          title="On Leave"
          value={stats.onLeave}
          subtitle="Temporarily unavailable"
          icon={MdWarning}
          iconBgColor="bg-warning-100 dark:bg-warning-900"
          iconColor="text-warning-600 dark:text-warning-400"
        />
        <StatCard
          title="Terminated"
          value={stats.terminated}
          subtitle="No longer employed"
          icon={MdPerson}
          iconBgColor="bg-neutral-100 dark:bg-neutral-800"
          iconColor="text-neutral-600 dark:text-neutral-400"
        />
      </div>

      {/* Filters and Search */}
      <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
            Flight Attendants ({filteredFlightAttendants.length})
          </h3>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <SearchBar
              searchTerm={searchTerm}
              onSearchChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search flight attendants..."
            />
            <div className="flex items-center space-x-3">
              <FilterBar
                filterValue={statusFilter}
                onFilterChange={(e) => setStatusFilter(e.target.value)}
                filterOptions={[
                  { value: 'all', label: 'All Flight Attendants' },
                  { value: 'active', label: `Active (${stats.active})` },
                  { value: 'on_leave', label: `On Leave (${stats.onLeave})` },
                  { value: 'terminated', label: `Terminated (${stats.terminated})` },
                ]}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Flight Attendant List */}
      <FlightAttendantList
        flightAttendants={filteredFlightAttendants}
        onViewFlightAttendant={handleViewFlightAttendant}
        onEditFlightAttendant={handleEditFlightAttendant}
        onDeleteFlightAttendant={handleDeleteFlightAttendant}
      />
    </div>
  )
}

export default FlightAttendantPage
